/** @jsx jsx */
import { jsx } from '@emotion/react';

import { Typography2 } from '@coursera/cds-core';

import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import ACECreditRecommendationInfo from 'bundles/unified-description-page-common/components/modules/credits/ACECreditRecommendationInfo';
import EligibleDegreesInfo from 'bundles/unified-description-page-common/components/modules/credits/EligibleDegreesInfo';
import { EligibleDegreesModule } from 'bundles/unified-description-page-common/components/modules/credits/EligibleDegreesModule';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';
import getEligibleDegreesDescription from 'bundles/unified-description-page-common/utils/getEligibleDegreesDescription';
import getEligibleDegreesTitle from 'bundles/unified-description-page-common/utils/getEligibleDegreesTitle';
import getVariantTitle from 'bundles/unified-description-page-common/utils/getVariantTitle';
import isForCreditNonDegree from 'bundles/unified-description-page-common/utils/isForCreditNonDegree';

import _t from 'i18n!nls/xdp';

type Props = {
  pageData: EnterpriseSpecializationPageFieldsFragment;
  config?: {
    disableCta?: boolean;
  };
};

const EnterpriseEligibleDegreesModuleRenderer = ({ pageData, config }: Props) => {
  const features = useEnterpriseProductFeatures();

  // NOTE: `features.showStackabilityOnXdpForEnterpriseLearners` is intentionally checked before checking the experiment value.
  // this is to avoid unnecessary impressions in the EPIC.
  // DO NOT switch the order of the checks.
  if (!features?.stackabilityOnXdpForEnterpriseLearners) {
    return null;
  }
  // don't show the stackability module if the similar "Earn for credit" is already displayed
  if (pageData.isSelectedForCredit) {
    return null;
  }

  const hasDegrees = pageData.degrees && pageData.degrees.length > 0;
  const hasACELink = pageData.productTags?.linkToACECreditRecommendation;

  if (!hasDegrees && !hasACELink) {
    return null;
  }

  const eligibleDegreesTitle = hasDegrees
    ? getEligibleDegreesTitle(pageData.degrees ?? [])
    : _t('Get a head start on your degree');

  const isFCND = isForCreditNonDegree(pageData?.slug, pageData?.partners?.[0]?.shortName || '');
  const variantTitle = getVariantTitle(pageData.productVariant);

  const { description: eligibleDegreesDescription, disclaimer: eligibleDegreesDisclaimer } =
    getEligibleDegreesDescription(
      variantTitle,
      pageData.productVariant,
      pageData.degrees?.[0]?.upsellPartners?.[0]?.name,
      pageData.degrees?.[0]?.upsellType,
      isFCND,
      pageData?.partners?.[0]?.name
    );

  return (
    <EligibleDegreesModule
      title={
        <Typography2 variant="titleSmall" component="h2" color="body">
          {eligibleDegreesTitle}
        </Typography2>
      }
    >
      {hasDegrees && (
        <EligibleDegreesInfo
          modalTitle={eligibleDegreesTitle}
          description={eligibleDegreesDescription}
          disclaimer={eligibleDegreesDisclaimer}
          degrees={pageData.degrees}
          disableCta={config?.disableCta}
        />
      )}
      {hasACELink && (
        <ACECreditRecommendationInfo
          variantTitle={variantTitle}
          // @ts-expect-error URL: string
          aceLink={pageData.productTags?.linkToACECreditRecommendation}
          disableCta={config?.disableCta}
        />
      )}
    </EligibleDegreesModule>
  );
};

export default EnterpriseEligibleDegreesModuleRenderer;
